<template>
  <div class="sf-el single-choice-input">
    <div class="sf-single-choice">
      <label class="sf-el__label">
        {{ label }}
        <span v-if="required" class="sf-el__required">*</span>
      </label>
      <p v-if="caption" class="sf-el__caption">{{ caption }}</p>

      <!-- Select (dropdown) display mode -->
      <template v-if="display === 'select'">
        <select
          class="sf-select"
          :value="modelValue"
          @change="handleInput($event.target.value)"
        >
          <option value="" disabled selected>
            {{ dummy ? 'Selecteer...' : $t('form.fields.choice.select_placeholder', 'Selecteer een optie...') }}
          </option>
          <option
            v-for="(choice, index) in filteredChoices"
            :key="index"
            :value="choice.id"
            :disabled="choice.disabled"
          >
            {{ choice.content }}
          </option>
        </select>
      </template>

      <!-- Radio button display modes -->
      <template v-else>
        <div v-if="add_search_bar" class="sf-search-bar">
          <input
            type="text"
            v-model="searchQuery"
            :placeholder="dummy ? 'Search...' : $t('form.fields.choice.search_placeholder')"
            class="sf-search-input"
          />
        </div>
        <div
          class="sf-single-choice__grid"
          :class="{ [`sf-single-choice__grid--display-${display}`]: display }"
        >
          <div
            v-for="(choice, index) in filteredChoices"
            :key="index"
            class="choice"
            @click="!choice.disabled && handleInput(choice.id)"
            :class="{
              'is-selected': modelValue === choice.id,
              'is-disabled': choice.disabled,
            }"
          >
            <input
              type="radio"
              :name="name"
              :value="choice.id"
              :checked="modelValue === choice.id"
              :disabled="choice.disabled"
              @change="handleInput(choice.id)"
              class="radio-button"
              :id="`${choice.id}-choice-${index}`"
            />
            <label :for="`${choice.id}-choice-${index}`">
              <img :src="choice.image" class="choice__img" alt="Choice Image" v-if="choice.image" />
              <div class="choice__label">
                <div class="choice__radio"></div>
                <div class="choice__label-content">
                  <span class="choice__title">
                    {{ choice.content }}
                  </span>
                  <span class="choice__description" v-if="choice.description">
                    {{ choice.description }}
                  </span>
                  <span v-if="choice.disabled" class="choice__unavailable">
                    Niet beschikbaar bij huidige configuratie
                  </span>
                </div>
              </div>
            </label>
          </div>
        </div>
        <div v-if="shouldShowMoreButton && !dummy" class="sf-show-more">
          <button type="button" @click="showAllChoices = true" class="sf-show-more-btn">
            {{ $t('form.fields.choice.show_more', { count: remainingChoicesCount }) }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import BaseIcon from '../../BaseIcon.vue';
  import { watch, ref, computed } from 'vue';
  export default {
    name: 'SingleChoiceElement',
    components: {
      BaseIcon,
    },
    props: {
      label: String,
      description: String,
      caption: String,
      choices: Array,
      name: String,
      modelValue: String,
      display: [String, Number],
      required: Boolean,
      add_search_bar: Boolean,
      dummy: { type: Boolean, default: false },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
      const searchQuery = ref('');
      const showAllChoices = ref(false);

      const handleInput = (value) => {
        emit('update:modelValue', value);
      };

      const filteredChoices = computed(() => {
        if (!props.add_search_bar) {
          // No search bar - show all choices
          return props.choices || [];
        }

        if (!searchQuery.value.trim()) {
          // Search bar enabled but no query
          if (showAllChoices.value) {
            // Show all choices if "show more" was clicked
            return props.choices || [];
          } else {
            // Show maximum 12 choices by default
            return props.choices ? props.choices.slice(0, 12) : [];
          }
        }

        const query = searchQuery.value.toLowerCase().trim();
        const filtered = props.choices
          ? props.choices.filter(
              (choice) =>
                choice.content.toLowerCase().includes(query) ||
                (choice.description && choice.description.toLowerCase().includes(query))
            )
          : [];

        // Show maximum 12 filtered results
        return filtered.slice(0, 12);
      });

      const shouldShowMoreButton = computed(() => {
        if (!props.add_search_bar || showAllChoices.value || searchQuery.value.trim()) {
          return false;
        }
        const totalChoices = props.choices ? props.choices.length : 0;
        return totalChoices > 12;
      });

      const remainingChoicesCount = computed(() => {
        const totalChoices = props.choices ? props.choices.length : 0;
        return Math.max(0, totalChoices - 12);
      });

      return {
        handleInput,
        searchQuery,
        filteredChoices,
        showAllChoices,
        shouldShowMoreButton,
        remainingChoicesCount,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@shared/assets/css/base/mixins.scss';
  @import './styles/choices.scss';

  .sf-select {
    width: 100%;
    padding: 0.625rem 2.5rem 0.625rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
    color: var(--cs-text-body, #374151);
    background-color: var(--cs-input-default-background, #fff);
    border: 1px solid var(--cs-input-default-border, #d1d5db);
    border-radius: var(--cs-border-radius, 0.5rem);
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.75rem center;
    background-size: 1rem;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: var(--cs-input-hover-border, #9ca3af);
    }

    &:focus {
      outline: none;
      border-color: var(--cs-brand-primary, #3b82f6);
      box-shadow: 0 0 0 3px var(--cs-brand-primary-light, rgba(59, 130, 246, 0.15));
    }
  }

  .sf-search-bar {
    margin-bottom: 1rem;
  }

  .sf-show-more {
    margin-top: 1rem;
    text-align: center;
  }

  .sf-show-more-btn {
    background: none;
    border: none;
    color: var(--cs-text-body);
    cursor: pointer;
    font-size: 0.875rem;
    text-decoration: underline;
    padding: 0.5rem;

    &:hover {
      color: var(--cs-button-default-background);
    }

    &:focus {
      outline: none;
      color: var(--cs-button-default-background);
    }
  }
</style>
